import React, { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import useDebouncedCallbackPerKey from 'hooks/useDebounceCallbackPerKey';
import InputNumber from 'layout/atoms/InputNumber';
import Loader from 'layout/atoms/Loader/Loader';
import Switch from 'layout/atoms/Switch/Switch';
import TableGrid, { Body, Cell, Head, Row } from 'layout/atoms/TableGrid/TableGrid';
import Grid from 'layout/molecules/Grid/Grid';
import { fetchOrderSuppliers, selectOrdersCreatorItem, selectOrdersCreatorRefetchFlags, selectOrdersCreatorStatus, selectOrdersCreatorSuppliers, setOrderComponentVariantConsumptionOverride, setOrderComponentVariantToNotReady, setOrderComponentVariantToReady, } from 'store/ordersCreatorSlice';
function OrderVolume() {
    const { t } = useTranslation(['common', 'orders', 'orderOperators']);
    const dispatch = useDispatch();
    const status = useSelector(selectOrdersCreatorStatus).fetchOrderVolume;
    const switchStatus = useSelector(selectOrdersCreatorStatus).other;
    const refetchFlag = useSelector(selectOrdersCreatorRefetchFlags).fetchOrderVolume;
    const orderItem = useSelector(selectOrdersCreatorItem);
    const orderSuppliers = useSelector(selectOrdersCreatorSuppliers);
    useEffect(() => {
        if (status === 'idle' || refetchFlag) {
            dispatch(fetchOrderSuppliers(orderItem.id));
        }
    }, [dispatch, orderItem.id, status, refetchFlag]);
    const [debouncedUpdateModelVariants, savingKeys] = useDebouncedCallbackPerKey((name, value) => {
        dispatch(setOrderComponentVariantConsumptionOverride({ orderComponentVariantId: name, value }));
    }, 1000);
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        debouncedUpdateModelVariants(name, value);
    };
    const handleSwitchChange = (value, id) => {
        if (value) {
            dispatch(setOrderComponentVariantToNotReady(id));
        }
        else {
            dispatch(setOrderComponentVariantToReady(id));
        }
    };
    const isFirstLoad = (status === 'loading' || status === 'idle') && orderSuppliers.length === 0;
    if (isFirstLoad) {
        return (React.createElement(Grid, { gap: 6, hp: 16, vp: 16 },
            React.createElement(Loader, null)));
    }
    return (React.createElement("div", { style: { position: 'relative' } },
        React.createElement(Grid, { gap: 0, hp: 16, vp: 12 }, orderSuppliers.map((item) => (React.createElement("div", { key: item.id },
            React.createElement("h3", null, item.orderSupplier.supplier.fullName),
            React.createElement(TableGrid, { columns: [
                    ['120px', '12%'],
                    ['100px', '10%'],
                    ['120px', '12%'],
                    ['150px', '15%'],
                    ['150px', '15%'],
                    ['100px', '10%'],
                    ['120px', '14%'],
                    ['80px', '8%'],
                ] },
                React.createElement(Head, null,
                    React.createElement(Row, { head: true },
                        React.createElement(Cell, null, t('orderOperators:order_operator_capitalize')),
                        React.createElement(Cell, null, t('orders:component_type')),
                        React.createElement(Cell, null, t('common:name')),
                        React.createElement(Cell, null, t('orders:component_params')),
                        React.createElement(Cell, null, t('orders:component_variant_params')),
                        React.createElement(Cell, null, t('orders:component_calculated_consumption')),
                        React.createElement(Cell, null, t('orders:component_order_volume')),
                        React.createElement(Cell, null, t('orders:component_ready')))),
                React.createElement(Body, null, item.componentVariants.map((variant) => (React.createElement(Row, { key: variant.id },
                    React.createElement(Cell, null, variant.orderOperatorOfOrder.orderOperator.name),
                    React.createElement(Cell, null, variant.componentCategory.typeLabelPL),
                    React.createElement(Cell, null, variant.name),
                    React.createElement(Cell, null, variant.componentAttributesPL),
                    React.createElement(Cell, null, variant.variantAttributesPL),
                    React.createElement(Cell, null,
                        variant.calculatedConsumption,
                        ' ',
                        variant.componentCategory.consumptionType.labelPL),
                    React.createElement(Cell, null,
                        React.createElement(InputNumber, { name: variant.id, value: variant.calculatedConsumption, onChange: handleInputChange, loading: savingKeys.has(variant.id), disabled: !variant.consumptionEditAvailable, decimal: 2 })),
                    React.createElement(Cell, null,
                        React.createElement(Switch, { value: variant.ready, onChange: () => handleSwitchChange(variant.ready, variant.id) }))))))))))),
        (switchStatus === 'loading' || status === 'loading') && React.createElement(Loader, { overlay: true })));
}
export default memo(OrderVolume);
